// Корень SPA с учётными записями: пока нет входа - экраны «Первый запуск» (создать администратора, только на
// компьютере-сервере), «Вход», «Регистрация» (по ссылке-приглашению или открытая), «Смена временного пароля»;
// после входа - приложение (children) и окно повторного входа при потере сессии посреди работы.
// data-testid форм совпадают со страницей входа сервера (loginpage.py): login-form, register-form, setup-form…
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { applyTheme } from '../../ui/uiState';
import { Icon } from '../../ui/icons';
import { authApi, authStore, logout, reloadInfo, showView, signedIn, useAuth } from './session';
import { Check, ErrorBox, errorOf, LOGIN_HINT, LOGIN_RE, PasswordField, passwordChecks, Rules, TextField } from './fields';
import { Reauth } from './Reauth';

export function AuthRoot({ children }: { children: ReactNode }) {
  const phase = useAuth((s) => s.phase);
  useEffect(() => { applyTheme(); }, []);
  if (phase === 'app') return <>{children}<Reauth /></>;
  if (phase === 'boot') return <div className="au-page au-boot" aria-busy="true"><Icon name="spinner" className="spin" size={22} /></div>;
  return <AuthPage />;
}

function AuthPage() {
  const phase = useAuth((s) => s.phase);
  const view = useAuth((s) => s.view);
  const error = useAuth((s) => s.error);
  let body: ReactNode;
  if (phase === 'fatal') {
    body = (
      <div className="au-form on" data-testid="auth-fatal">
        <h1>Нет связи с сервером</h1>
        <p className="au-sub">{error || 'Сервер редактора не отвечает.'} Проверьте, что редактор запущен, и обновите страницу.</p>
        <button type="button" className="au-submit" onClick={() => location.reload()}>Обновить страницу</button>
      </div>
    );
  } else if (phase === 'change') body = <ChangeForm />;
  else if (view === 'setup') body = <SetupForm />;
  else if (view === 'noSetup') body = <NoSetup />;
  else if (view === 'register') body = <RegisterForm />;
  else if (view === 'reset') body = <ResetForm />;
  else body = <LoginForm />;
  return (
    <div className="au-page">
      <div className="au-side" aria-hidden>
        <div className="au-side-inner">
          <div className="au-logo">СТО</div>
          <div className="au-side-title">Редактор отчётов<br />по СТО САФУ 2026</div>
          <ul className="au-side-list">
            <li><Icon name="sparkles" size={16} /> Пишете содержание - оформление, нумерация и титул делаются сами</li>
            <li><Icon name="check" size={16} /> Проверка по СТО той же проверкой, что и «sto check»</li>
            <li><Icon name="docx" size={16} /> Готовый DOCX и PDF с закладками</li>
            <li><Icon name="lock" size={16} /> У каждого свои отчёты - чужие не видны</li>
          </ul>
        </div>
      </div>
      <main className="au-main">
        <div className="au-card">
          <div className="au-brand"><span className="au-brand-logo">СТО</span> Редактор СТО САФУ</div>
          {body}
        </div>
      </main>
    </div>
  );
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; kind: 'error' | 'wait' }>({ text: '', kind: 'error' });
  const run = (fn: () => Promise<void>) => async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr({ text: '', kind: 'error' });
    try { await fn(); } catch (ex: any) { setErr(errorOf(ex)); } finally { setBusy(false); }
  };
  return { busy, err, setErr, run };
}

function Submit({ busy, label, testid }: { busy: boolean; label: string; testid: string }) {
  return (
    <button type="submit" className="au-submit" disabled={busy} data-testid={testid}>
      {busy && <Icon name="spinner" className="spin" size={16} />}{label}
    </button>
  );
}

// ------------------------------------------------------------------------------------------------ вход
function LoginForm() {
  const info = useAuth((s) => s.info);
  const [login, setLogin] = useState('');
  const [pw, setPw] = useState('');
  const [remember, setRemember] = useState(false);
  const { busy, err, setErr, run } = useSubmit();
  const submit = run(async () => {
    if (!login.trim() || !pw) { setErr({ text: 'Введите логин и пароль', kind: 'error' }); return; }
    const me = await authApi.login(login.trim(), pw, remember);
    signedIn(me, pw);
  });
  const reg = info?.registration || 'invite';
  return (
    <form className="au-form on" data-testid="login-form" onSubmit={submit} autoComplete="on" noValidate>
      <h1>Вход</h1>
      <p className="au-sub">Войдите, чтобы открыть свои отчёты.</p>
      <TextField label="Логин" value={login} onChange={setLogin} testid="login-login" autoComplete="username" autoFocus />
      <PasswordField label="Пароль" value={pw} onChange={setPw} testid="login-password" autoComplete="current-password" />
      <Check checked={remember} onChange={setRemember} label="Запомнить меня на этом устройстве" testid="login-remember"
        hint="Не отмечайте на чужом или общем компьютере" />
      <ErrorBox text={err.text} kind={err.kind} />
      <Submit busy={busy} label="Войти" testid="login-submit" />
      <div className="au-links">
        {reg !== 'closed'
          ? <>Нет учётной записи? <button type="button" className="au-link" onClick={() => showView('register')} data-testid="to-register">
            {reg === 'invite' ? 'Регистрация по приглашению' : 'Регистрация'}</button></>
          : <span className="au-muted">Нет учётной записи? Её создаёт администратор сервера.</span>}
      </div>
      <p className="au-foot"><button type="button" className="au-link" onClick={() => showView('reset')} data-testid="to-reset">Забыли пароль?</button></p>
    </form>
  );
}

// ------------------------------------------------------------------------------------------------ сброс пароля
function ResetForm() {
  const [login, setLogin] = useState('');
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const { busy, err, setErr, run } = useSubmit();
  const request = run(async () => {
    if (!login.trim()) { setErr({ text: 'Введите логин', kind: 'error' }); return; }
    await authApi.resetRequest(login.trim());
    setSent(true);
  });
  const confirm = run(async () => {
    if (!/^\d{6}$/.test(code.trim())) { setErr({ text: 'Код - 6 цифр из сообщения бота', kind: 'error' }); return; }
    if (pw.length < 8) { setErr({ text: 'Пароль слишком короткий: нужно не меньше 8 символов', kind: 'error' }); return; }
    if (pw !== pw2) { setErr({ text: 'Пароли не совпадают', kind: 'error' }); return; }
    await authApi.resetConfirm(login.trim(), code.trim(), pw);
    setDone(true);
  });
  const back = <div className="au-links"><button type="button" className="au-link" onClick={() => showView('login')} data-testid="reset-back">Ко входу</button></div>;
  if (done) {
    return (
      <div className="au-form on" data-testid="reset-done">
        <h1>Пароль изменён</h1>
        <p className="au-sub">Теперь войдите с новым паролем. Сеансы на других устройствах завершены.</p>
        {back}
      </div>
    );
  }
  if (!sent) {
    return (
      <form className="au-form on" data-testid="reset-form" onSubmit={request} noValidate>
        <h1>Сброс пароля</h1>
        <p className="au-sub">Код придёт в Telegram, если он привязан к учётной записи (меню учётной записи → «Привязать Telegram»).</p>
        <TextField label="Логин" value={login} onChange={setLogin} testid="reset-login" autoComplete="username" autoFocus />
        <ErrorBox text={err.text} kind={err.kind} />
        <Submit busy={busy} label="Получить код" testid="reset-request" />
        {back}
        <p className="au-foot">Telegram не привязан? Напишите <a href="https://t.me/MrSpavn" target="_blank" rel="noopener noreferrer">@MrSpavn</a>: выдам временный пароль.</p>
      </form>
    );
  }
  return (
    <form className="au-form on" data-testid="reset-confirm-form" onSubmit={confirm} noValidate>
      <h1>Введите код</h1>
      <p className="au-sub">Если к учётной записи <b>{login.trim()}</b> привязан Telegram, бот прислал туда код. Он действует 10 минут.</p>
      <TextField label="Код из Telegram" value={code} onChange={setCode} testid="reset-code" autoComplete="one-time-code" autoFocus />
      <PasswordField label="Новый пароль" value={pw} onChange={setPw} testid="reset-new" autoComplete="new-password" />
      <PasswordField label="Новый пароль ещё раз" value={pw2} onChange={setPw2} testid="reset-new2" autoComplete="new-password" />
      <Rules items={passwordChecks(pw, pw2, login.trim())} />
      <ErrorBox text={err.text} kind={err.kind} />
      <Submit busy={busy} label="Сменить пароль" testid="reset-submit" />
      {back}
      <p className="au-foot">Код не пришёл за минуту? Напишите <a href="https://t.me/MrSpavn" target="_blank" rel="noopener noreferrer">@MrSpavn</a>.</p>
    </form>
  );
}

// ------------------------------------------------------------------------------------------------ регистрация
function RegisterForm() {
  const info = useAuth((s) => s.info);
  const urlInvite = useAuth((s) => s.invite);
  const urlRef = useAuth((s) => s.ref);
  const [invite, setInvite] = useState(urlInvite);
  const [ref, setRef] = useState(urlRef);
  const [login, setLogin] = useState('');
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [remember, setRemember] = useState(false);
  const { busy, err, setErr, run } = useSubmit();
  const mode = info?.registration || 'invite';
  const badInvite = !!urlInvite && info?.inviteValid === false;
  const needCode = mode === 'invite' && (!urlInvite || badInvite);
  const checks = passwordChecks(pw, pw2, login, info?.passwordRules.minLength || 8);
  const back = () => {
    authStore.setState({ invite: '', ref: '' });
    history.replaceState(null, '', '/login' + location.hash);
    void reloadInfo();
    showView('login');
  };
  if (mode === 'closed' || badInvite) {
    return (
      <div className="au-form on" data-testid={mode === 'closed' ? 'register-closed' : 'register-bad-invite'}>
        <h1>{mode === 'closed' ? 'Регистрация закрыта' : 'Приглашение недействительно'}</h1>
        <ErrorBox kind={mode === 'closed' ? 'info' : 'error'}
          text={mode === 'closed' ? 'Учётные записи на этом сервере создаёт администратор - обратитесь к нему. Ссылки-приглашения сейчас не действуют.'
            : 'Приглашение недействительно: оно истекло, отозвано или уже использовано. Попросите у администратора новую ссылку.'} />
        <button type="button" className="au-submit" onClick={back} data-testid="to-login">Войти</button>
      </div>
    );
  }
  const submit = run(async () => {
    if (needCode && !invite.trim() && !ref.trim()) throw new Error('Введите код приглашения из ссылки администратора или ник того, кто вас пригласил');
    if (!LOGIN_RE.test(login.trim())) throw new Error('Логин: ' + LOGIN_HINT.toLowerCase());
    if (pw.length < (info?.passwordRules.minLength || 8)) throw new Error(`Пароль слишком короткий: нужно не меньше ${info?.passwordRules.minLength || 8} символов`);
    if (pw !== pw2) throw new Error('Пароли не совпадают');
    const me = await authApi.register({ login: login.trim(), password: pw, name: name.trim(), invite: (needCode ? invite : urlInvite).trim() || undefined, ref: ref.trim() || undefined, remember });
    signedIn(me, pw);
  });
  return (
    <form className="au-form on" data-testid="register-form" onSubmit={submit} autoComplete="on" noValidate>
      <h1>Регистрация</h1>
      <p className="au-sub">{(urlInvite && !badInvite) || urlRef ? 'Вас пригласили в редактор отчётов. Придумайте логин и пароль.'
        : mode === 'invite' ? 'Регистрация - по приглашению: введите код из ссылки или ник того, кто вас пригласил.' : 'Новая учётная запись.'}</p>
      {<>
          {needCode && !urlRef && <TextField label="Код приглашения" value={invite} onChange={setInvite} testid="register-invite" autoComplete="off" />}
          <TextField label={needCode ? 'Ник пригласившего' : 'Ник пригласившего (необязательно)'} value={ref} onChange={setRef} testid="register-ref" autoComplete="off" maxLength={64} />
          <TextField label="Логин" value={login} onChange={setLogin} testid="register-login" autoComplete="username" autoFocus={!needCode}
            hint={LOGIN_HINT} maxLength={32} />
          <TextField label="Имя и фамилия (необязательно)" value={name} onChange={setName} testid="register-name" autoComplete="name"
            hint="Так вас увидит администратор. Данные для титула - потом, в «Мой профиль»." />
          <PasswordField label="Пароль" value={pw} onChange={setPw} testid="register-password" autoComplete="new-password" />
          <PasswordField label="Пароль ещё раз" value={pw2} onChange={setPw2} testid="register-password2" autoComplete="new-password" />
          <Rules items={checks} />
          <Check checked={remember} onChange={setRemember} label="Запомнить меня на этом устройстве" testid="register-remember" />
          <ErrorBox text={err.text} kind={err.kind} />
          <Submit busy={busy} label="Зарегистрироваться" testid="register-submit" />
        </>}
      <div className="au-links"><button type="button" className="au-link" onClick={back} data-testid="to-login">У меня уже есть учётная запись</button></div>
    </form>
  );
}

// ------------------------------------------------------------------------------------------------ первый запуск
function SetupForm() {
  const info = useAuth((s) => s.info);
  const urlCode = useAuth((s) => s.setupCode);
  const [code, setCode] = useState(urlCode);
  const [remember, setRemember] = useState(true);
  const [login, setLogin] = useState('admin');
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const { busy, err, run } = useSubmit();
  const checks = passwordChecks(pw, pw2, login, info?.passwordRules.minLength || 8);
  const submit = run(async () => {
    if (!LOGIN_RE.test(login.trim())) throw new Error('Логин: ' + LOGIN_HINT.toLowerCase());
    if (pw.length < (info?.passwordRules.minLength || 8)) throw new Error(`Пароль слишком короткий: нужно не меньше ${info?.passwordRules.minLength || 8} символов`);
    if (pw !== pw2) throw new Error('Пароли не совпадают');
    if (!code.trim()) throw new Error('Введите код первого запуска - он напечатан в окне сервера редактора');
    const me = await authApi.setup({ login: login.trim(), password: pw, name: name.trim(), remember, setupCode: code.trim() });
    signedIn(me, pw);
  });
  const legacy = info?.legacyProjects || 0;
  return (
    <form className="au-form on" data-testid="setup-form" onSubmit={submit} noValidate>
      <div className="au-step"><Icon name="sparkles" size={14} /> Первый запуск</div>
      <h1>Создайте аккаунт администратора</h1>
      <p className="au-sub">Администратор управляет пользователями и приглашениями, видит и может править все отчёты на этом
        сервере. Пароль знаете только вы - запишите его в надёжное место.</p>
      <TextField label="Логин администратора" value={login} onChange={setLogin} testid="setup-login" autoComplete="username" hint={LOGIN_HINT} maxLength={32} />
      <TextField label="Имя и фамилия (необязательно)" value={name} onChange={setName} testid="setup-name" autoComplete="name" />
      <PasswordField label="Пароль" value={pw} onChange={setPw} testid="setup-password" autoComplete="new-password" autoFocus />
      <PasswordField label="Пароль ещё раз" value={pw2} onChange={setPw2} testid="setup-password2" autoComplete="new-password" />
      <Rules items={checks} />
      {!urlCode && <TextField label="Код первого запуска" value={code} onChange={setCode} testid="setup-code" autoComplete="off"
        hint={'Проще всего - запустите «Редактор СТО.cmd»: открытая им страница подставит код сама. Иначе код - в окне сервера'
          + (info?.setupCodeFile ? ' или в файле ' + info.setupCodeFile : '') + '.'} />}
      <Check checked={remember} onChange={setRemember} label="Запомнить меня на этом компьютере (30 дней)" testid="setup-remember"
        hint="Снимите, если компьютером-сервером пользуются другие люди" />
      {legacy > 0 && (
        <div className="au-notice" data-testid="setup-legacy"><Icon name="inbox" size={18} />
          <span>Найдено отчётов прежней версии: <b>{legacy}</b>. Они станут отчётами администратора.</span></div>
      )}
      <ErrorBox text={err.text} kind={err.kind} />
      <Submit busy={busy} label="Создать администратора" testid="setup-submit" />
      <p className="au-foot"><Icon name="info" size={13} /> Забыли пароль - на этом компьютере:
        <code> python web\server\manage.py reset-password ЛОГИН</code></p>
    </form>
  );
}

function NoSetup() {
  const info = useAuth((s) => s.info);
  // за прокси (https://сайт) порта в адресе страницы нет - настоящий адрес сообщает сервер
  const url = info?.localUrl || `http://127.0.0.1:${location.port || '8765'}/`;
  return (
    <div className="au-form on" data-testid="setup-remote">
      <h1>Сервер ещё не настроен</h1>
      <p className="au-sub">Учётную запись администратора создают на компьютере, где запущен редактор: откройте там
        <b> {url}</b> (или запустите «Редактор СТО.cmd»). После этого здесь появится вход.</p>
      <button type="button" className="au-submit" onClick={() => location.reload()}>Проверить ещё раз</button>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ смена временного пароля
function ChangeForm() {
  const me = useAuth((s) => s.me);
  const last = useAuth((s) => s.lastPassword);
  const [old, setOld] = useState(last);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const { busy, err, run } = useSubmit();
  const checks = passwordChecks(pw, pw2, me?.user.login || '');
  const submit = run(async () => {
    if (!old) throw new Error('Введите текущий (временный) пароль');
    if (pw.length < 8) throw new Error('Пароль слишком короткий: нужно не меньше 8 символов');
    if (pw !== pw2) throw new Error('Пароли не совпадают');
    if (pw === old) throw new Error('Новый пароль должен отличаться от временного');
    const m = await authApi.changePassword(old, pw);
    signedIn(m);
  });
  return (
    <form className="au-form on" data-testid="change-form" onSubmit={submit} noValidate>
      <div className="au-step au-step-user"><Icon name="key" size={14} /> {me ? me.user.login : ''}</div>
      <h1>Смените временный пароль</h1>
      <p className="au-sub">Администратор выдал вам временный пароль. Придумайте свой - временный после этого перестанет действовать.</p>
      {!last && <PasswordField label="Временный пароль" value={old} onChange={setOld} testid="change-old" autoComplete="current-password" autoFocus />}
      {last && <input type="password" hidden readOnly value={old} autoComplete="current-password" aria-hidden />}
      <PasswordField label="Новый пароль" value={pw} onChange={setPw} testid="change-new" autoComplete="new-password" autoFocus={!!last} />
      <PasswordField label="Новый пароль ещё раз" value={pw2} onChange={setPw2} testid="change-new2" autoComplete="new-password" />
      <Rules items={checks} />
      <ErrorBox text={err.text} kind={err.kind} />
      <Submit busy={busy} label="Сменить пароль и войти" testid="change-submit" />
      <div className="au-links"><button type="button" className="au-link" onClick={() => void logout()} data-testid="change-logout">Выйти</button></div>
    </form>
  );
}
